'use client';

import { useEffect, useRef, useState } from 'react';
import { getClient } from '@/lib/supabase/client';
import { useRouter } from 'next/navigation';
import { prepareGuestMediaHandoff } from '@/lib/auth/guest-media-handoff-client';
import {
  invalidateClientCache,
  notifyCreditsUpdated,
  notifySubscriptionUpdated,
  CLIENT_CACHE_KEYS,
} from '@/lib/client-request-cache';

const AUTH_INIT_RETRY_DELAYS = [0, 200, 500];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const generateNonce = async () => {
  const nonce = btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(32))));
  const encodedNonce = new TextEncoder().encode(nonce);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encodedNonce);
  const hashedNonce = Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');

  return { nonce, hashedNonce };
};

const syncServerSession = async (session) => {
  if (!session || typeof session === 'string') return;

  const response = await fetch('/auth/callback', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      event: 'SIGNED_IN',
      session,
    }),
  });

  if (!response.ok) {
    const result = await response.json().catch(() => ({}));
    throw new Error(result.error || 'Failed to sync auth session');
  }
};

const initializeUser = async (supabase) => {
  let lastError = null;

  for (const delayMs of AUTH_INIT_RETRY_DELAYS) {
    if (delayMs > 0) {
      await sleep(delayMs);
    }

    const { data: sessionData } = await supabase.auth.getSession();
    if (sessionData?.session) {
      await syncServerSession(sessionData.session);
    }

    const response = await fetch('/api/auth/init-user', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    const result = await response.json().catch(() => ({}));

    if (response.ok) {
      return result;
    }

    lastError = new Error(result.error || 'Failed to initialize user');

    if (response.status !== 401) {
      break;
    }
  }

  throw lastError || new Error('Failed to initialize user');
};

export default function GoogleOneTap() {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [interactionReady, setInteractionReady] = useState(false);
  const supabase = getClient();
  const router = useRouter();
  const isProd = process.env.NODE_ENV === 'production';
  const promptTriggeredRef = useRef(false);
  const storageKey = 'sora2_google_one_tap_prompted';

  const isLocalEnvironment = () => {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0' ||
      hostname === '::1' ||
      hostname.endsWith('.local')
    );
  };

  const hasPrompted = () => {
    if (typeof window === 'undefined') return false;
    if (promptTriggeredRef.current) return true;
    try {
      return sessionStorage.getItem(storageKey) === 'true';
    } catch (error) {
      console.warn('Google One Tap: sessionStorage unavailable', error);
      return false;
    }
  };

  const markPrompted = () => {
    promptTriggeredRef.current = true;
    try {
      sessionStorage.setItem(storageKey, 'true');
    } catch (error) {
      console.warn('Google One Tap: failed to persist prompt state', error);
    }
  };

  // 方案 A：首次用户交互后才加载 One Tap
  useEffect(() => {
    if (isLocalEnvironment()) return;
    if (hasPrompted()) return;

    const handleInteraction = () => {
      setInteractionReady(true);
    };

    window.addEventListener('pointerdown', handleInteraction, { once: true });
    window.addEventListener('keydown', handleInteraction, { once: true });
    window.addEventListener('touchstart', handleInteraction, { once: true, passive: true });
    window.addEventListener('scroll', handleInteraction, { once: true, passive: true });

    return () => {
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
    };
  }, []);

  // 加载 Google GIS 脚本
  useEffect(() => {
    if (isLocalEnvironment()) return;
    if (!interactionReady) return;
    if (hasPrompted()) return;

    const existingScript = document.querySelector('script[data-google-one-tap="true"]');
    if (existingScript) {
      if (window.google?.accounts?.id) {
        setScriptLoaded(true);
      } else {
        existingScript.addEventListener('load', () => setScriptLoaded(true), { once: true });
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-google-one-tap', 'true');
    script.onload = () => setScriptLoaded(true);
    document.body.appendChild(script);
  }, [interactionReady]);

  // 初始化并显示 One Tap
  useEffect(() => {
    if (isLocalEnvironment()) return;
    if (!interactionReady || !scriptLoaded) return;
    if (hasPrompted()) return;

    if (!process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID) {
      console.error('Google One Tap: Missing NEXT_PUBLIC_GOOGLE_CLIENT_ID');
      return;
    }

    const initializeGoogleOneTap = async () => {
      // 检查全局 google 对象是否存在
      if (!window.google) return;
      const { nonce, hashedNonce } = await generateNonce();

      window.google.accounts.id.initialize({
        client_id: process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID,
        nonce: hashedNonce,
        callback: async (response) => {
          try {
            await prepareGuestMediaHandoff(supabase);
            const { data, error } = await supabase.auth.signInWithIdToken({
              provider: 'google',
              token: response.credential,
              nonce,
            });

            if (error) throw error;

            const verifiedSession =
              data?.session ||
              (await supabase.auth.getSession()).data?.session ||
              null;

            if (verifiedSession) {
              await syncServerSession(verifiedSession);
            }

            const initResult = await initializeUser(supabase);

            invalidateClientCache(CLIENT_CACHE_KEYS.subscription);
            invalidateClientCache(CLIENT_CACHE_KEYS.credits);
            notifySubscriptionUpdated();
            notifyCreditsUpdated();

            console.log('Google One Tap sign in success');

            if (initResult.requireInvite) {
              window.location.href = '/?require_invite=true';
              return;
            }

            router.refresh();
          } catch (error) {
            console.error('Error signing in with Google One Tap:', error);
          }
        },
        // 禁用自动选择，强制用户确认（可选，根据需求调整）
        auto_select: false,
        // 防止在用户点击取消后进入长时间的冷静期（开发调试用，生产环境建议移除或设为 false）
        cancel_on_tap_outside: true,
        // 生产环境启用 FedCM，避免浏览器弃用提示；本地保持关闭便于调试
        use_fedcm_for_prompt: isProd,
      });

      // 显示 One Tap 提示，并在控制台输出通知状态
      if (isProd) {
        window.google.accounts.id.prompt();
      } else {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('Google One Tap not displayed:', notification.getNotDisplayedReason());
          } else if (notification.isSkippedMoment()) {
            console.log('Google One Tap skipped:', notification.getSkippedReason());
          } else if (notification.isDismissedMoment()) {
            console.log('Google One Tap dismissed:', notification.getDismissedReason());
          }
        });
      }
    };

    initializeGoogleOneTap()
      .then(() => {
        markPrompted();
      })
      .catch((error) => {
        console.error('Google One Tap initialization failed:', error);
      });
  }, [interactionReady, scriptLoaded, supabase, router, isProd]);

  return null; // 此组件不渲染任何可见的 DOM 元素
}
